import type { CalendarCellProps } from "@tql/ui-components-react/kendo/dateinputs";
import {
	Calendar,
	CalendarCell,
} from "@tql/ui-components-react/kendo/dateinputs";
import type { FieldRenderProps } from "@tql/ui-components-react/kendo/form";
import { FieldWrapper } from "@tql/ui-components-react/kendo/form";
import { Error, Hint, Label } from "@tql/ui-components-react/kendo/labels";
import { DatePicker } from "@tql/ui-components-react/tql/dateinputs";
import type { CSSProperties } from "react";
import { useCallback, useEffect, useMemo } from "react";

import type { Holiday } from "~/shared/schemas/holiday-schema";
import {
	isHoliday,
	isWeekend,
	parseRobustDate,
} from "~/shared/utils/date-helpers.ts";

type FieldRenderPropsDateInput = {
	label: string;
	id: string;
	disabled: boolean;
	hint: boolean;
	wrapperStyle: CSSProperties | undefined;
	hintDirection: "start" | "end" | undefined;
	disabledDates?: Holiday[];
	min?: Date;
} & FieldRenderProps;

export const FormDatePicker = (fieldRenderProps: FieldRenderPropsDateInput) => {
	const {
		validationMessage,
		touched,
		label,
		id,
		valid,
		disabled,
		hint,
		wrapperStyle,
		hintDirection,
		disabledDates,
		min,
		onChange,
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Safe destructuring of value prop with proper type checking below
		value: valueProp,
		...others
	} = fieldRenderProps;

	// Explicitly type value as Date | string | number | undefined
	const value: Date | string | number | undefined =
		typeof valueProp === "string" ||
		typeof valueProp === "number" ||
		valueProp instanceof Date
			? valueProp
			: undefined;

	const CustomCalendarCell = useMemo(() => {
		const Cell = (props: CalendarCellProps) => {
			const { value: date } = props;

			const isBeforeMinimum = min !== undefined && date < min;

			const isDisabled =
				isWeekend(date) || isHoliday(date, disabledDates) || isBeforeMinimum;

			return (
				<CalendarCell
					{...props}
					className={`${props.className || ""} ${isDisabled ? "k-disabled k-state-disabled" : ""}`.trim()}
					isDisabled={isDisabled}
				/>
			);
		};
		Cell.displayName = "CustomCalendarCell";
		return Cell;
	}, [disabledDates, min]);

	// Helper function to find the next valid date (not weekend or holiday)
	const getNextValidDate = useCallback(
		(startDate: Date = new Date()): Date => {
			const date = new Date(startDate);
			date.setHours(0, 0, 0, 0);

			const checkDate = new Date(date);

			// Find the next valid date
			while (true) {
				if (!isWeekend(checkDate) && !isHoliday(checkDate, disabledDates)) {
					return checkDate;
				}

				checkDate.setDate(checkDate.getDate() + 1);
			}
		},
		[disabledDates],
	);

	const CustomCalendar = useMemo(() => {
		const CalendarWrapper = (props: React.ComponentProps<typeof Calendar>) => (
			<Calendar {...props} cell={CustomCalendarCell} />
		);
		CalendarWrapper.displayName = "CustomCalendar";
		return CalendarWrapper;
	}, [CustomCalendarCell]);

	// Ensure the current value is valid (not weekend or holiday)
	const getValidValue = (): Date | undefined => {
		const parsedDate = parseRobustDate(value);
		if (!parsedDate) {
			return getNextValidDate();
		}

		return parsedDate;
	};

	const validValue = getValidValue();

	useEffect(() => {
		if (!value) {
			const initialValidDate = getNextValidDate();
			onChange({ value: initialValidDate });
		}
	}, [value, getNextValidDate, onChange]);

	const showValidationMessage: string | false | null =
		(touched || !valid) && typeof validationMessage === "string"
			? validationMessage
			: null;
	const showHint: boolean = !showValidationMessage && hint;
	const hintId: string = showHint ? `${id}_hint` : "";
	const errorId: string = showValidationMessage ? `${id}_error` : "";
	const labelId: string = label ? `${id}_label` : "";

	return (
		<FieldWrapper style={wrapperStyle}>
			<Label
				className={"k-form-label"}
				editorDisabled={disabled}
				editorId={id}
				editorValid={valid}
				id={labelId}
			>
				{label}
			</Label>
			<div className={"k-form-field-wrap"}>
				<DatePicker
					aria-describedby={`${hintId} ${errorId}`}
					ariaLabelledBy={labelId}
					calendar={CustomCalendar}
					disabled={disabled}
					id={id}
					min={min}
					onChange={onChange}
					valid={valid}
					value={validValue}
					{...others}
				/>
				{showHint ? (
					<Hint direction={hintDirection} id={hintId}>
						{hint}
					</Hint>
				) : undefined}
				{showValidationMessage ? (
					<Error id={errorId}>{showValidationMessage}</Error>
				) : undefined}
			</div>
		</FieldWrapper>
	);
};
